import type { TimelineEvent } from "./types";
import { TIMELINE_ENTITY_TYPES } from "./activityTypes";

/**
 * Presentation-layer filtering for the workflow Timeline.
 *
 * Filtering lives client-side, over the already-loaded feed, on purpose:
 * the Activity Ledger read API is by-entity only (no server-side
 * activity-type predicate), and the merged feed the BFF returns is
 * bounded (per-stream page limit + a merged cap). The real visibility
 * horizon is therefore the per-stream page the BFF fetched — which no
 * client- *or* BFF-side filter can see past without adding keyset
 * pagination and an activity-type filter to the ledger service itself.
 * Pushing this filter server-side would add contract surface for no gain
 * against that horizon, so the correct design is to filter the loaded
 * window and let the existing `truncated` disclosure stay honest about
 * what isn't on screen.
 *
 * Everything here is pure so the filter decisions are unit-testable
 * without React or the network.
 */

export interface TimelineFilterState {
    /** Selected work-item id, or null for "all activity". */
    readonly workItemId: string | null;
    /** Activity types the user has hidden. Empty = show everything. */
    readonly hiddenActivityTypes: ReadonlySet<string>;
}

/** True when any filter axis is narrowing the feed. */
export function isTimelineFiltered(filter: TimelineFilterState): boolean {
    return filter.workItemId !== null || filter.hiddenActivityTypes.size > 0;
}

/**
 * Apply both filter axes to the merged feed. Work-item selection keeps
 * only events keyed on that work item (workflow-level events drop out,
 * which is intended: "show me this item's history"); the activity-type
 * set hides matching verbs. Order is preserved (the input is already
 * newest-first).
 */
export function applyTimelineFilters(
    events: readonly TimelineEvent[],
    filter: TimelineFilterState,
): readonly TimelineEvent[] {
    if (!isTimelineFiltered(filter)) return events;
    return events.filter((event) => {
        if (filter.workItemId !== null) {
            if (
                event.entityType !== TIMELINE_ENTITY_TYPES.workItem ||
                event.entityId !== filter.workItemId
            ) {
                return false;
            }
        }
        if (filter.hiddenActivityTypes.has(event.activityType)) return false;
        return true;
    });
}

/**
 * Distinct activity types present in the feed, in first-seen order
 * (the feed is newest-first, so this is recency-ordered). Drives the
 * activity-type filter so it lists only verbs that actually occur on
 * this workflow — no dead toggles, and unknown/forward-compat types
 * surface here too rather than being silently unfilterable.
 */
export function presentActivityTypes(events: readonly TimelineEvent[]): readonly string[] {
    const seen = new Set<string>();
    const ordered: string[] = [];
    for (const event of events) {
        if (!seen.has(event.activityType)) {
            seen.add(event.activityType);
            ordered.push(event.activityType);
        }
    }
    return ordered;
}

/**
 * Distinct work-item ids that actually appear in the feed. Used to prune
 * the work-item filter rail to items that have audit history (an item
 * with no events would be a dead filter option).
 */
export function presentWorkItemIds(events: readonly TimelineEvent[]): ReadonlySet<string> {
    const ids = new Set<string>();
    for (const event of events) {
        if (event.entityType === TIMELINE_ENTITY_TYPES.workItem && event.entityId.length > 0) {
            ids.add(event.entityId);
        }
    }
    return ids;
}

const ACTIVITY_TYPE_LABELS: Readonly<Record<string, string>> = {
    "workflow.created": "Workflow created",
    "workflow.child.spawned": "Child workflow spawned",
    "workflow.edited": "Workflow edited",
    "workitem.created": "Item added",
    "workitem.routed": "Item routed",
    "workitem.broadcasted": "Broadcast",
    "workitem.addressed": "Filed",
    "workitem.replied": "Reply",
    "workitem.completed": "Completed",
    "workitem.completed.by-reply": "Answered",
    "workitem.queues.changed": "Queue changed",
    "workitem.edited": "Edited",
    "workitem.removed": "Removed",
    "workitem.approved": "Approved",
    "workitem.rejected": "Rejected",
    "workitem.snoozed": "Snoozed",
    "workitem.rescheduled": "Rescheduled",
    "note.created": "Note added",
    "note.edited": "Note edited",
    "workitem.viewed": "Viewed",
};

/**
 * Human label for an activity type in the filter list. Intentionally a
 * small, separate vocabulary from the row headlines in
 * <c>WorkflowTimeline</c> (a terse filter chip vs. a sentence). An
 * unrecognized producer-side addition is still labeled and filterable:
 * it is humanized from the key rather than shown raw.
 */
export function activityTypeLabel(activityType: string): string {
    return ACTIVITY_TYPE_LABELS[activityType] ?? humanizeActivityType(activityType);
}

/**
 * `workitem.locked` → "Locked"; `queue.member.added` → "Member added". The
 * first segment is the entity namespace and is dropped; the remaining
 * segments and any dashes become words, sentence-cased. A key with no
 * namespace is used whole.
 */
export function humanizeActivityType(activityType: string): string {
    const trimmed = activityType.trim();
    if (trimmed.length === 0) return activityType;
    const dot = trimmed.indexOf(".");
    const remainder = dot >= 0 && dot < trimmed.length - 1 ? trimmed.slice(dot + 1) : trimmed;
    const words = remainder
        .replace(/[.\-_]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
    if (words.length === 0) return activityType;
    return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Short label for a work item in the filter rail. Prefers the first
 * non-empty line of the body (the human-meaningful content), falling
 * back to the work-item type so tasks/messages without a body still read
 * sensibly.
 */
export function workItemFilterLabel(type: string, body: string | null | undefined): string {
    const trimmed = (body ?? "").trim();
    if (trimmed.length > 0) {
        const firstLine = trimmed.split("\n", 1)[0] ?? trimmed;
        return firstLine.length > 60 ? `${firstLine.slice(0, 57)}…` : firstLine;
    }
    switch (type) {
        case "task":
            return "Task";
        case "note":
            return "Note";
        case "message":
            return "Message";
        default:
            return type.length > 0 ? type : "Item";
    }
}

/** Parse the `hide` URL param (csv of activity types) into a set. */
export function parseHiddenActivityTypes(raw: string | null): ReadonlySet<string> {
    if (raw === null || raw.length === 0) return new Set<string>();
    return new Set(
        raw
            .split(",")
            .map((value) => value.trim())
            .filter((value) => value.length > 0),
    );
}

/** Serialize a hidden-types set back to a stable, comma-joined param. */
export function serializeHiddenActivityTypes(hidden: ReadonlySet<string>): string {
    // Sorted so the URL is stable regardless of toggle order (deep links
    // and the back button don't churn the param on equivalent state).
    return Array.from(hidden).sort().join(",");
}
